import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import PanelTemplate from "../../templates/Panel";
import PageHeader from "../../components/PageHeader";
import Box from "../../components/Box";
import Table from "../../components/Table";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import FilterSimple from "../../components/FilterSimple";
import Pagination from "../../components/Pagination";
import SortHeader from "../../components/SortHeader";
import { ModalDelete } from "../../components/ModalDelete";

import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { hasPermission } from "../../services/auth";
import { convertIntToMoney } from "../../helpers/conversions";
import { getEntryStatusLabel } from "../Finance/helpers";
import { useBillingCompanies } from "../Finance/billingCompany";

import PurchaseInvoiceTabs from "./tabs";
import ModalPurchaseInvoiceForm from "./ModalPurchaseInvoiceForm";
import ModalFilter from "./ModalFilter";
import { getInvoiceLabel } from "./helpers";

const searchParams = [
    { name: 'Fornecedor, número ou chave', key: '[number]', operation: 'contain', type: 'text' },
];

const indexMap = [
    { index: 'Emissão', value: 'issued_at' },
    { index: 'Fornecedor', value: 'company_trade_name' },
    { index: 'Nota', value: 'number' },
    { index: 'Valor', value: 'amount' },
];

const normalizePage = page => {
    const parsed = parseInt(page, 10);
    return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
};

const parseFilters = search => {
    const params = new URLSearchParams(search);

    return {
        page: normalizePage(params.get('page')),
        search: params.get('search') || '',
        issuedFrom: params.get('issued_from') || '',
        issuedTo: params.get('issued_to') || '',
        companyId: params.get('company_id') || '',
        billingCompanyId: params.get('billing_company_id') || '',
        link: params.get('link') || '',
        payment: params.get('payment') || '',
        ordenationItem: params.get('sort') || 'issued_at',
        orientation: params.get('orientation') || 'desc',
    };
};

const FILTER_KEYS = [
    ['search', 'search'],
    ['issuedFrom', 'issued_from'],
    ['issuedTo', 'issued_to'],
    ['companyId', 'company_id'],
    ['billingCompanyId', 'billing_company_id'],
    ['link', 'link'],
    ['payment', 'payment'],
];

const buildApiQuery = filters => {
    const params = new URLSearchParams();

    FILTER_KEYS.forEach(([key, param]) => {
        const value = (filters[key] ?? '').trim ? filters[key].trim() : filters[key];
        if (value) params.set(param, value);
    });

    params.set('ordenation_item', filters.ordenationItem);
    params.set('orientation', filters.orientation);
    params.set('page', filters.page);

    return params.toString();
};

const buildListSearch = filters => {
    const params = new URLSearchParams();

    if (filters.page > 1) params.set('page', filters.page);

    FILTER_KEYS.forEach(([key, param]) => {
        const value = (filters[key] ?? '').trim ? filters[key].trim() : filters[key];
        if (value) params.set(param, value);
    });

    if (filters.ordenationItem !== 'issued_at') params.set('sort', filters.ordenationItem);
    if (filters.orientation !== 'desc') params.set('orientation', filters.orientation);

    return params.toString();
};

/**
 * Notas de entrada (BDM-1050): as notas que os fornecedores emitem contra o
 * tenant. A tela existe separada do pedido de compra porque as notas chegam em
 * lote — cinco e-mails da XBZ numa tarde — e ninguém vai abrir compra por
 * compra para lançar cada uma.
 */
export const PurchaseInvoiceList = () => {
    const [loading, setLoading] = useState(true);
    const [invoices, setInvoices] = useState([]);
    const [data, setData] = useState({ header: [], rows: [] });
    const [totalAmount, setTotalAmount] = useState(0);
    const [pendingFromSefaz, setPendingFromSefaz] = useState(0);

    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [toDelete, setToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const filters = useMemo(() => parseFilters(location.search), [location.search]);

    const { billingCompanies } = useBillingCompanies();
    const canManage = hasPermission('manage_purchase_order');

    useEffect(() => {
        getInvoices();
    }, [location.search]);

    useEffect(() => {
        generateData();
    }, [invoices]);

    const updateUrl = (changes, resetPage = true) => {
        const next = { ...filters, ...changes, page: resetPage ? 1 : (changes.page || filters.page) };
        const search = buildListSearch(next);

        navigate({ pathname: location.pathname, search: search ? `?${search}` : '' });
    };

    const getInvoices = () => {
        setInvoices([]);
        setLoading(true);

        // Contador da aba da SEFAZ: é o que faz alguém lembrar de triar.
        api.get('/dfe-invoice/paginate?status=pending&per_page=1')
            .then(res => setPendingFromSefaz(res.data.pending ?? 0))
            .catch(() => setPendingFromSefaz(0));

        api.get(`/purchase-invoice/paginate?${buildApiQuery(filters)}`).then(res => {
            setInvoices(res.data.resources.data);
            setTotalAmount(res.data.total_amount ?? 0);
            setCurrentPage(res.data.resources.current_page);
            setLastPage(res.data.resources.last_page);
            setPerPage(res.data.resources.per_page);
            setTotalResults(res.data.resources.total);
            setHasNextPage(res.data.resources.next_page_url !== null);
            setHasPrevPage(res.data.resources.prev_page_url !== null);
        }).catch(renderError).then(() => setLoading(false));
    };

    const generateData = () => {
        const header = ['Emissão', 'Fornecedor', 'Nota', 'Valor', 'Compra', 'Conta a pagar', 'Anexos'];

        if (canManage) header.push('');

        const rows = invoices.map(invoice => {
            const purchaseOrders = invoice.purchase_orders ?? [];
            const entries = (invoice.finance_entries ?? []).filter(entry => entry.status !== 'canceled');

            return {
                action: canManage ? () => { setEditing(invoice); setShowForm(true); } : undefined,
                data: [
                    invoice.issued_at ? moment(invoice.issued_at).format('DD/MM/YYYY') : '—',
                    invoice.company_trade_name ?? '—',
                    getInvoiceLabel(invoice),
                    convertIntToMoney(invoice.amount, true),
                    purchaseOrders.length > 0
                        ? purchaseOrders.map(purchaseOrder => `#${purchaseOrder.code}`).join(', ')
                        : <span className={`purchase-invoice-unlinked`}>Sem compra vinculada</span>,
                    entries.length > 0
                        ? <span className={`finance-entry-status status-${entries[0].status}`}>
                            {entries.length > 1
                                ? `${entries.length} parcelas`
                                : getEntryStatusLabel(entries[0].status)
                            }
                        </span>
                        : '—',
                    <span className={`purchase-invoice-files`}>
                        {invoice.file_url &&
                            <a href={invoice.file_url} target={`_blank`} rel={`noreferrer`} onClick={event => event.stopPropagation()} title={`PDF da nota`}>
                                <Icon name={`pdf`} size={16} />
                            </a>
                        }
                        {invoice.xml_url &&
                            <a href={invoice.xml_url} target={`_blank`} rel={`noreferrer`} onClick={event => event.stopPropagation()} title={`XML da nota`}>
                                <Icon name={`xml`} size={16} />
                            </a>
                        }
                        {!invoice.file_url && !invoice.xml_url && '—'}
                    </span>,
                    ...(canManage ? [
                        <button
                            className={`purchase-invoice-delete`}
                            title={`Excluir nota`}
                            onClick={event => { event.stopPropagation(); setToDelete(invoice); }}
                        >
                            <Icon name={`delete`} size={16} />
                        </button>,
                    ] : []),
                ],
            };
        });

        setData({ header, rows });
    };

    const refresh = () => {
        if (location.search === '') {
            getInvoices();
        } else {
            navigate(location.pathname);
        }
    };

    const remove = () => {
        setDeleting(true);

        api.delete(`/purchase-invoice/${toDelete.id}`).then(() => {
            toast.success('Nota excluída');
            setToDelete(null);
            refresh();
        }).catch(renderError).then(() => setDeleting(false));
    };

    const hasCustomFilter = FILTER_KEYS.some(([key]) => key !== 'search' && filters[key]);

    return (
        <>
            <ModalPurchaseInvoiceForm
                show={showForm}
                close={() => { setShowForm(false); setEditing(null); }}
                invoice={editing}
                onSaved={refresh}
            />

            <ModalFilter
                show={showFilter}
                close={() => setShowFilter(false)}
                filters={filters}
                billingCompanies={billingCompanies}
                setCustomFilters={updateUrl}
            />

            <ModalDelete
                show={toDelete !== null}
                text={<p>Excluir a nota <strong>{getInvoiceLabel(toDelete)}</strong>? A compra volta a ficar sem ela, e a conta a pagar continua no financeiro.</p>}
                action={remove}
                loading={deleting}
                close={() => setToDelete(null)}
            />

            <PanelTemplate id="purchase-invoice-list">
                <PageHeader
                    left={
                        <>
                            <FilterSimple
                                params={searchParams}
                                filter={(value, metadata) => updateUrl({ search: metadata?.value ?? '' })}
                                externalValue={filters.search}
                                externalOption={searchParams[0].key}
                            />
                            <PurchaseInvoiceTabs pending={pendingFromSefaz} />
                        </>
                    }
                    right={
                        <>
                            <Button
                                type={hasCustomFilter ? `primary` : `secondary`}
                                size={`small`}
                                svg={`filter`}
                                text={`Filtrar`}
                                action={() => setShowFilter(true)}
                            />
                            {canManage &&
                                <Button
                                    type={`primary`}
                                    size={`small`}
                                    svg={`plus-white`}
                                    text={`Lançar nota`}
                                    action={() => { setEditing(null); setShowForm(true); }}
                                />
                            }
                        </>
                    }
                />

                {!loading && invoices.length > 0 &&
                    <div className={`purchase-invoice-context`}>
                        <span className={`purchase-invoice-total`}>
                            Total do período filtrado <strong>{convertIntToMoney(totalAmount, true)}</strong>
                        </span>
                    </div>
                }

                <Box className={`no-padding with-pagination`}>
                    <Table
                        header={data.header}
                        rows={data.rows}
                        loading={loading}
                        sortOrdenationAction={index => (
                            <SortHeader
                                index={index}
                                ordenationItem={filters.ordenationItem}
                                orientation={filters.orientation}
                                onSort={(item, orientation) => updateUrl({ ordenationItem: item, orientation })}
                                indexMap={indexMap}
                            />
                        )}
                    />
                </Box>

                {!loading &&
                    <Pagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        perPage={perPage}
                        totalResults={totalResults}
                        hasNextPage={hasNextPage}
                        hasPrevPage={hasPrevPage}
                        setCurrentPage={page => updateUrl({ page }, false)}
                    />
                }
            </PanelTemplate>
        </>
    );
};

export default PurchaseInvoiceList;
